import React from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const AboutHistory: React.FC = () => {
  const { elementRef: ref1, isVisible: isVisible1 } = useScrollAnimation();
  const { elementRef: ref2, isVisible: isVisible2 } = useScrollAnimation();
  const { elementRef: ref3, isVisible: isVisible3 } = useScrollAnimation();

  const milestones = [
    {
      year: "2022",
      title: "Fundação da BotoHarmony",
      description: "Nascemos com o propósito de revolucionar a estética em João Pessoa, trazendo a filosofia de que a verdadeira beleza está na naturalidade.",
      image: "/assets/img/tambia.jpg"
    },
    {
      year: "2023",
      title: "Expansão e Crescimento",
      description: "Inauguração da segunda unidade e expansão da equipe com especialistas certificados em harmonização facial e procedimentos avançados.",
      image: "/assets/img/doctor/Arielly .jpg"
    },
    {
      year: "2025",
      title: "Referência Regional",
      description: "Mais de 5000 clientes atendidos, consolidando-se como referência em tratamentos estéticos naturais e personalizados na Paraíba.",
      image: "/assets/img/doctor/Drª Brenda .jpg"
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-800 mb-6">
            Nossa <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">Jornada</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Uma história construída com dedicação, excelência e o compromisso de transformar vidas através da estética de alta performance.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line (Desktop) */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-secondary via-primary to-secondary transform -translate-x-1/2"></div>

          {/* Milestones */}
          <div className="space-y-24">
            {milestones.map((milestone, index) => {
              const ref = index === 0 ? ref1 : index === 1 ? ref2 : ref3;
              const isVisible = index === 0 ? isVisible1 : index === 1 ? isVisible2 : isVisible3;
              const isEven = index % 2 === 0;

              return (
                <div
                  key={index}
                  ref={ref as React.RefObject<HTMLDivElement>}
                  className={`relative transition-all duration-1000 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                    {/* Content */}
                    <div className={`${isEven ? 'lg:order-1 lg:text-right' : 'lg:order-2'} space-y-4`}>
                      {/* Year Badge */}
                      <div className={`inline-flex items-center ${isEven ? 'lg:ml-auto' : ''}`}>
                        <div className="relative group/badge">
                          <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary rounded-full blur-lg opacity-50 group-hover/badge:opacity-75 transition-opacity"></div>
                          <div className="relative bg-gradient-to-r from-secondary to-primary text-white px-8 py-3 rounded-full font-bold text-2xl shadow-lg">
                            {milestone.year}
                          </div>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 leading-tight">
                        {milestone.title}
                      </h3>

                      {/* Description */}
                      <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                        {milestone.description}
                      </p>

                      {/* Decorative Line */}
                      <div className={`w-24 h-1 bg-gradient-to-r from-secondary to-primary ${isEven ? 'lg:ml-auto' : ''}`}></div>
                    </div>

                    {/* Image */}
                    <div className={`${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                      <div className="relative group">
                        {/* Main Image */}
                        <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl transform transition-all duration-700 group-hover:scale-105 group-hover:shadow-3xl">
                          <img
                            src={milestone.image}
                            alt={milestone.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        </div>

                        {/* Decorative Element */}
                        <div className={`absolute -z-10 w-full h-full rounded-3xl bg-gradient-to-br from-secondary/20 to-primary/20 ${
                          isEven ? '-bottom-6 -right-6' : '-bottom-6 -left-6'
                        } group-hover:scale-105 transition-transform duration-500`}></div>
                      </div>
                    </div>
                  </div>

                  {/* Center Dot (Desktop) */}
                  <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary rounded-full blur-md animate-pulse"></div>
                      <div className="relative w-6 h-6 bg-gradient-to-r from-secondary to-primary rounded-full border-4 border-white shadow-lg"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-24 text-center">
          <div className="inline-block p-8 bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-xl border border-gray-100">
            <p className="text-xl md:text-2xl font-serif text-gray-800 mb-2">
              E essa é apenas o <span className="text-secondary font-bold">começo</span>
            </p>
            <p className="text-gray-600">
              Continuamos evoluindo para oferecer sempre o melhor em estética
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutHistory;
